import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  User, Mail, Lock, Eye, EyeOff, Phone, Loader2,
  LogIn, UserPlus, ArrowLeft, Check, Store, Users, Shield, ShoppingBag, Globe
} from 'lucide-react';
import { authService } from '../../services/authService';
import { useIdioma } from '../../i18n/IdiomaContext';
import TermosDeUso from './TermosDeUso';

const TEST_ACCOUNTS_PUBLICO = [
  { label: 'Fornecedor', email: 'fornecedor@clickhub.teste', senha: 'ClickHub2026!', icon: Store, color: 'text-blue-400 border-blue-800 bg-blue-500/10 hover:bg-blue-500/20' },
  { label: 'Afiliado Brasil', email: 'afiliado@clickhub.teste', senha: 'ClickHub2026!', icon: ShoppingBag, color: 'text-green-400 border-green-800 bg-green-500/10 hover:bg-green-500/20' },
  { label: 'Afiliado Internacional', email: 'afiliado.usa@clickhub.teste', senha: 'ClickHub2026!', icon: Globe, color: 'text-cyan-400 border-cyan-800 bg-cyan-500/10 hover:bg-cyan-500/20' },
];

const TEST_ACCOUNTS_ADMIN = [
  { label: 'Admin', email: 'admin@clickhub.teste', senha: 'ClickHub2026!', icon: Shield, color: 'text-red-400 border-red-800 bg-red-500/10 hover:bg-red-500/20' },
];

// CORRIGIDO (achado da revisão): estes botões preenchem e-mail e senha
// de contas de teste reais - se ficassem visíveis em produção, qualquer
// visitante da tela de login conseguiria entrar como admin sem saber
// nenhuma senha de verdade. Agora só aparecem quando
// REACT_APP_MOSTRAR_CONTAS_TESTE=true estiver definido no ambiente -
// nunca por padrão em produção.
const MOSTRAR_CONTAS_TESTE = process.env.REACT_APP_MOSTRAR_CONTAS_TESTE === "true";

const AuthPage = ({ onLogin, modoAdmin = false }) => {
  const { idioma, setIdioma, t, idiomas } = useIdioma();
  const [mode, setMode] = useState('login'); // login, registro, forgot
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [metodoLogin, setMetodoLogin] = useState('senha'); // senha, certificado, google
  const [arquivoCertificado, setArquivoCertificado] = useState(null);
  const [mostrarTermos, setMostrarTermos] = useState(false);
  
  // Campos do formulário
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    senha: '',
    confirmarSenha: '',
    telefone: '',
    tipoUsuario: 'afiliado',
    aceiteTermos: false
  });
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  // ======================
  // LOGIN
  // ======================
  
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.senha) {
      toast.error('Preencha email e senha');
      return;
    }
    
    try {
      setLoading(true);
      const result = await authService.login(formData.email, formData.senha);
      toast.success(`Bem-vindo, ${result.user.nome}!`);
      onLogin(result.user);
    } catch (error) {
      toast.error(error.message || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  // Login por certificado digital - exclusivo do modo admin. O admin
  // sobe o mesmo arquivo de certificado (.pfx/.p12/.cer) que já
  // cadastrou antes; o backend confere o hash e libera acesso sem senha.
  const handleLoginCertificado = async (e) => {
    e.preventDefault();
    if (!formData.email || !arquivoCertificado) {
      toast.error('Informe o e-mail e selecione o arquivo do certificado.');
      return;
    }
    try {
      setLoading(true);
      const dadosForm = new FormData();
      dadosForm.append('email', formData.email);
      dadosForm.append('file', arquivoCertificado);
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/certificados-admin/login-por-certificado`, {
        method: 'POST',
        body: dadosForm,
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || 'Erro ao entrar com certificado');
      authService.salvarTokens(data);
      toast.success(`Bem-vindo, ${data.user.nome}!`);
      onLogin(data.user);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // ======================
  // REGISTRO
  // ======================
  
  const handleRegistro = async (e) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.email || !formData.senha) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    if (formData.senha !== formData.confirmarSenha) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    if (formData.senha.length < 6) {
      toast.error('A senha deve ter no mínimo 6 caracteres');
      return;
    }

    if (!formData.aceiteTermos) {
      toast.error('Você precisa aceitar os Termos de Uso e a Política de Privacidade para se cadastrar.');
      return;
    }
    
    try {
      setLoading(true);
      const result = await authService.registrar({ ...formData, tipo: formData.role, aceite_termos: formData.aceiteTermos });
      toast.success('Conta criada com sucesso!');
      onLogin(result.user);
    } catch (error) {
      toast.error(error.message || 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };
  
  // ======================
  // ESQUECI SENHA
  // ======================
  
  const handleForgot = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      toast.error('Informe seu email');
      return;
    }
    
    try {
      setLoading(true);
      await authService.esqueciSenha(formData.email);
      toast.success('Se o email existir, você receberá instruções para recuperar sua senha.');
      setMode('login');
    } catch (error) {
      toast.error(error.message || 'Erro ao solicitar recuperação');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 flex items-center justify-center px-4 py-6 overflow-y-auto">
      {/* Background decorativo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl" />
      </div>
      
      <div className={`w-full relative z-10 transition-all ${mode === 'registro' ? 'max-w-lg' : 'max-w-md'}`}>
        {/* Seletor de idioma - visível em qualquer parte do mundo, para
            brasileiro ou não, dentro ou fora do Brasil */}
        <div className="flex justify-end mb-3">
          <select
            value={idioma}
            onChange={(e) => setIdioma(e.target.value)}
            className="bg-gray-900/80 border border-gray-800 text-gray-300 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-orange-500"
          >
            {idiomas.map((op) => (
              <option key={op.codigo} value={op.codigo}>
                {op.bandeira} {op.nome}
              </option>
            ))}
          </select>
        </div>

        {/* Logo */}
        <div className="text-center mb-4 sm:mb-8">
          <img src="/images/clickhub-logo.png" alt="ClickHub" className="w-12 h-12 mx-auto mb-2 object-contain" />
          <h1 className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
            ClickHub
          </h1>
          {modoAdmin ? (
            <p className="text-red-400 mt-2 font-medium">🔒 {t('auth.painelAdministrativo', 'Painel Administrativo Interno')}</p>
          ) : (
            <p className="text-gray-400 mt-2">{t('auth.plataformaDescricao', 'Plataforma de Vendas em Marketplaces')}</p>
          )}
        </div>
        
        <Card className="bg-gray-900/80 backdrop-blur-lg border-gray-800 p-4 sm:p-6 max-h-[calc(100dvh-8rem)] overflow-y-auto">
          {/* Tabs de Login/Registro - no modo admin, ninguém se
              auto-cadastra, então a aba de "Criar Conta" nem aparece */}
          {mode !== 'forgot' && !modoAdmin && (
            <div className="flex gap-2 mb-6">
              <Button
                variant={mode === 'login' ? 'default' : 'outline'}
                className={`flex-1 ${mode === 'login' ? 'bg-orange-500 hover:bg-orange-600' : 'border-gray-700'}`}
                onClick={() => setMode('login')}
              >
                <LogIn className="w-4 h-4 mr-2" />
                Entrar
              </Button>
              <Button
                variant={mode === 'registro' ? 'default' : 'outline'}
                className={`flex-1 ${mode === 'registro' ? 'bg-orange-500 hover:bg-orange-600' : 'border-gray-700'}`}
                onClick={() => setMode('registro')}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Criar Conta
              </Button>
            </div>
          )}
          
          {/* Formulário de Login */}
          {mode === 'login' && (
            <>
            <div className="space-y-4">
              {/* Seletor de método de login - certificado digital só
                  aparece no modo admin (mais camadas de segurança pra
                  quem tem acesso administrativo) */}
              <div className={`grid gap-2 ${modoAdmin ? "grid-cols-3" : "grid-cols-2"}`}>
                <button
                  type="button"
                  onClick={() => setMetodoLogin('senha')}
                  className={`py-2 rounded-lg text-xs font-medium transition-colors ${
                    metodoLogin === 'senha' ? 'bg-orange-500 text-white' : 'bg-gray-800 text-gray-400 border border-gray-700'
                  }`}
                >
                  Email e Senha
                </button>
                {modoAdmin && (
                  <button
                    type="button"
                    onClick={() => setMetodoLogin('certificado')}
                    className={`py-2 rounded-lg text-xs font-medium transition-colors ${
                      metodoLogin === 'certificado' ? 'bg-orange-500 text-white' : 'bg-gray-800 text-gray-400 border border-gray-700'
                    }`}
                  >
                    Certificado Digital
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setMetodoLogin('google')}
                  className={`py-2 rounded-lg text-xs font-medium transition-colors ${
                    metodoLogin === 'google' ? 'bg-orange-500 text-white' : 'bg-gray-800 text-gray-400 border border-gray-700'
                  }`}
                >
                  Google
                </button>
              </div>
            </div>

            {metodoLogin === 'senha' && (
            <form onSubmit={handleLogin} className="space-y-4">
              {/* Acesso Rápido - Contas de Teste - só visível quando
                  REACT_APP_MOSTRAR_CONTAS_TESTE=true, nunca em produção real.
                  Mostra só contas de admin na rota secreta, só contas
                  públicas (fornecedor/afiliado) na rota normal. */}
              {MOSTRAR_CONTAS_TESTE && (
              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-2 text-center uppercase tracking-wider">Acesso Rapido (Teste)</p>
                <div className={`grid gap-2 ${modoAdmin ? "grid-cols-1" : "grid-cols-3"}`}>
                  {(modoAdmin ? TEST_ACCOUNTS_ADMIN : TEST_ACCOUNTS_PUBLICO).map((acc) => {
                    const Icon = acc.icon;
                    return (
                      <button
                        key={acc.email}
                        type="button"
                        data-testid={`quick-login-${acc.label.toLowerCase()}`}
                        onClick={() => {
                          handleChange('email', acc.email);
                          handleChange('senha', acc.senha);
                        }}
                        className={`p-2 rounded-lg border text-center transition-all ${acc.color}`}
                      >
                        <Icon className="w-4 h-4 mx-auto mb-1" />
                        <span className="text-[10px] font-medium leading-tight block">{acc.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              )}

              <div className="border-t border-gray-800 pt-4">
                <label className="block text-sm text-gray-300 mb-1">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    type="email"
                    placeholder="seu@email.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="bg-gray-800 border-gray-700 pl-10"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-300 mb-1">Senha</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••"
                    value={formData.senha}
                    onChange={(e) => handleChange('senha', e.target.value)}
                    className="bg-gray-800 border-gray-700 pl-10 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <Button
                type="submit"
                className="w-full bg-orange-500 hover:bg-orange-600 h-11"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <LogIn className="w-4 h-4 mr-2" />
                )}
                Entrar
              </Button>

              <button
                type="button"
                onClick={() => setMode('forgot')}
                className="w-full text-sm text-gray-400 hover:text-orange-400 transition-colors"
              >
                Esqueceu sua senha?
              </button>
            </form>
            )}

            {metodoLogin === 'certificado' && (
              <form onSubmit={handleLoginCertificado} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      type="email"
                      placeholder="seu@email.com"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Arquivo do certificado (.pfx, .p12 ou .cer)</label>
                  <input
                    type="file"
                    accept=".pfx,.p12,.cer"
                    onChange={(e) => setArquivoCertificado(e.target.files?.[0] || null)}
                    className="w-full text-sm text-gray-400 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-gray-800 file:text-gray-300"
                  />
                  {!arquivoCertificado && (
                    <p className="text-xs text-gray-500 mt-1">Precisa ser o mesmo arquivo já cadastrado na sua conta.</p>
                  )}
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-orange-600 hover:bg-orange-500">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LogIn className="w-4 h-4 mr-2" />}
                  Entrar com Certificado
                </Button>
              </form>
            )}

            {metodoLogin === 'google' && (
              <div className="space-y-4">
                {/* Login com Google - PENDENTE DE CONFIGURAÇÃO: precisa de
                    um Client ID do Google Cloud Console. Avisa
                    honestamente em vez de fingir que funciona. */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-gray-700"
                  onClick={() => toast.info("Login com Google ainda não configurado - fale com o administrador do sistema.")}
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Entrar com Google
                </Button>
                <p className="text-center text-xs text-gray-500">
                  Assim que configurado, um clique aqui já entra direto com sua conta Google.
                </p>
              </div>
            )}
          </>
          )}
          
          {/* Formulário de Registro */}
          {mode === 'registro' && (
            <form onSubmit={handleRegistro} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-300 mb-1">Nome completo *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    type="text"
                    placeholder="Seu nome"
                    value={formData.nome}
                    onChange={(e) => handleChange('nome', e.target.value)}
                    className="bg-gray-800 border-gray-700 pl-10"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      type="email"
                      placeholder="seu@email.com"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="bg-gray-800 border-gray-700 pl-10"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-300 mb-1">Telefone</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      type="tel"
                      placeholder="(11) 99999-9999"
                      value={formData.telefone}
                      onChange={(e) => handleChange('telefone', e.target.value)}
                      className="bg-gray-800 border-gray-700 pl-10"
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-300 mb-2">Tipo de conta *</label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => handleChange('tipoUsuario', 'afiliado')}
                    className={`p-3 rounded-lg border transition-all ${
                      formData.tipoUsuario === 'afiliado'
                        ? 'border-orange-500 bg-orange-500/10'
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <Users className={`w-6 h-6 mx-auto mb-1 ${formData.tipoUsuario === 'afiliado' ? 'text-orange-400' : 'text-gray-400'}`} />
                    <p className={`text-sm font-medium ${formData.tipoUsuario === 'afiliado' ? 'text-orange-400' : 'text-gray-300'}`}>
                      {t('auth.afiliado', 'Afiliado')}
                    </p>
                    <p className="text-xs text-gray-500">{t('auth.afiliadoDescricao', 'Venda produtos')}</p>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => handleChange('tipoUsuario', 'fornecedor')}
                    className={`p-3 rounded-lg border transition-all ${
                      formData.tipoUsuario === 'fornecedor'
                        ? 'border-orange-500 bg-orange-500/10'
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <Store className={`w-6 h-6 mx-auto mb-1 ${formData.tipoUsuario === 'fornecedor' ? 'text-orange-400' : 'text-gray-400'}`} />
                    <p className={`text-sm font-medium ${formData.tipoUsuario === 'fornecedor' ? 'text-orange-400' : 'text-gray-300'}`}>
                      {t('auth.fornecedor', 'Fornecedor')}
                    </p>
                    <p className="text-xs text-gray-500">{t('auth.fornecedorDescricao', 'Forneça produtos')}</p>
                  </button>

                  {/* Afiliado USA - deixado visível e pronto por pedido
                      do dono do sistema, mas desabilitado (não
                      clicável): o painel Internacional ainda está em
                      construção, pausado de propósito. Quando for
                      ativado de verdade, basta remover o "disabled" e
                      conectar ao fluxo de cadastro internacional. */}
                  <button
                    type="button"
                    disabled
                    className="relative p-3 rounded-lg border border-gray-800 opacity-50 cursor-not-allowed"
                  >
                    <Globe className="w-6 h-6 mx-auto mb-1 text-gray-500" />
                    <p className="text-sm font-medium text-gray-500">{t('auth.afiliadoUsa', 'Afiliado USA')}</p>
                    <p className="text-xs text-gray-600">{t('auth.emBreve', 'Em breve')}</p>
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Senha *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Mínimo 6 caracteres"
                      value={formData.senha}
                      onChange={(e) => handleChange('senha', e.target.value)}
                      className="bg-gray-800 border-gray-700 pl-10 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-300 mb-1">Confirmar senha *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Repita a senha"
                      value={formData.confirmarSenha}
                      onChange={(e) => handleChange('confirmarSenha', e.target.value)}
                      className="bg-gray-800 border-gray-700 pl-10"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="aceite-termos"
                  checked={formData.aceiteTermos}
                  onChange={(e) => handleChange('aceiteTermos', e.target.checked)}
                  className="mt-1"
                />
                <label htmlFor="aceite-termos" className="text-xs text-gray-400 leading-relaxed">
                  Li e aceito os{' '}
                  <button
                    type="button"
                    onClick={() => setMostrarTermos(true)}
                    className="text-orange-400 underline hover:text-orange-300"
                  >
                    Termos de Uso e a Política de Privacidade
                  </button>
                </label>
              </div>
              
              <Button
                type="submit"
                className="w-full bg-orange-500 hover:bg-orange-600 h-11"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Criar Conta
              </Button>
            </form>
          )}
          
          {/* Formulário Esqueci Senha */}
          {mode === 'forgot' && (
            <form onSubmit={handleForgot} className="space-y-4">
              <button
                type="button"
                onClick={() => setMode('login')}
                className="flex items-center text-sm text-gray-400 hover:text-white mb-4"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Voltar ao login
              </button>
              
              <h2 className="text-xl font-bold text-white mb-2">Recuperar senha</h2>
              <p className="text-gray-400 text-sm mb-4">
                Informe seu email para receber instruções de recuperação.
              </p>
              
              <div>
                <label className="block text-sm text-gray-300 mb-1">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    type="email"
                    placeholder="seu@email.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="bg-gray-800 border-gray-700 pl-10"
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                className="w-full bg-orange-500 hover:bg-orange-600 h-11"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4 mr-2" />
                )}
                Enviar instruções
              </Button>
            </form>
          )}
        </Card>
        
        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          © 2025 ClickHub. Todos os direitos reservados.
        </p>
      </div>

      {mostrarTermos && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto py-8">
          <div className="bg-gray-950 border border-gray-800 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <TermosDeUso onVoltar={() => setMostrarTermos(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthPage;
